/** @jsx jsx */

import { ChangeEvent } from 'react';
import { camelCase, get } from 'lodash';

import { Colors, Grid, LegacyButton, TextInput } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import CodeEditor from 'app/developer-v3/components/CodeEditor';
import FieldMessage, {
  FieldType,
} from 'app/developer-v3/components/FieldMessage';

import type { DeepLinkParams } from 'app/developer-v3/types';

const Styles = {
  inputWrapper: borderColor => [
    borderColor &&
      css`
        border: 1px solid ${borderColor};
        border-radius: 4px;
      `,
  ],
  textInput: css`
    border-radius: 4px 0 0 4px !important;
    border-right: 1px solid ${Colors.GrayWarm4};
    border-top: none;
    border-bottom: none;
    border-left: none;
  `,
  fieldMessageWrapper: css`
    display: flex;
    width: 100%;
  `,
};

type Props = {
  codeData?: string;
  isDisabled: boolean;
  meta?: {
    info?: DeepLinkParams;
  };
  onBlur?: () => void;
  onCancelCode: null | (() => void);
  onChangeCode: (value: string) => void;
  onChangeText: (value: string) => void;
  onClearAllCode: null | (() => void);
  onToggleEditCode: () => void;
  placeholder?: string;
  shouldEditCode: boolean;
  textData?: string;
  textLabel: string;
};

function TextOrCodeInput(props: Props) {
  const {
    codeData,
    isDisabled,
    meta,
    onBlur,
    onCancelCode,
    onChangeCode,
    onChangeText,
    onClearAllCode,
    onToggleEditCode,
    placeholder,
    shouldEditCode,
    textData,
    textLabel,
  } = props;

  const info = get(meta, 'info', {});

  return (
    <div>
      {shouldEditCode ? (
        <CodeEditor
          mode="javascript"
          onCancelCode={onCancelCode}
          onChange={(value = '') => onChangeCode(value)}
          onClearAllCode={onClearAllCode}
          value={codeData}
        />
      ) : (
        <Grid columns="2fr auto" gap={8}>
          <TextInput
            aria-label={textLabel}
            css={Styles.textInput}
            isReadonly={isDisabled}
            onBlur={onBlur}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onChangeText(event.target.value)
            }
            placeholder={placeholder}
            value={textData}
          />
          <LegacyButton
            aria-label="Edit Code"
            color="secondary"
            disabled={isDisabled}
            iconBefore="arrowCode"
            onClick={onToggleEditCode}
          >
            Edit Code
          </LegacyButton>
        </Grid>
      )}
      {info && (
        <div css={Styles.fieldMessageWrapper}>
          <FieldMessage
            fieldKey="sample-output-editor"
            message={info.message}
            type={camelCase(`info ${info.type}`) as FieldType}
          />
        </div>
      )}
    </div>
  );
}

TextOrCodeInput.defaultProps = {
  textData: '',
  codeData: '',
  onClearAllCode: null,
  onCancelCode: null,
};

export default TextOrCodeInput;
